import React, {useEffect, useState} from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {faClock, faEnvelope} from "@fortawesome/free-regular-svg-icons";

import { setUserInformation, setDashboardContext, setEffectivePermissions, updateDashboardContext } from "../../../redux-store/ReduxStore.js";
import { resolveLandingPath } from "../../../utils/rbacUtils.js";
import { UserAuth } from '../../../authentication/AuthContext.jsx';
import { isEmailVerified } from '../../../authentication/emailVerification.js';
import { PrimaryButton, SecondaryButton, TertiaryButton, LinkButton } from "../../../components/button/Button.jsx";
import { Input, Password } from "../../../components/form/Input.jsx";
import ThirdPartyLogins from './ThirdPartyLogins.jsx';
import { GeoPlayLogo } from "../../../shell/header/GeoPlayLogo.jsx";
import EmailIcon from "../../../assets/common/email.svg?react";

import { getUser, buildPreferencesPatch } from "../../../services/UserProfileService.jsx";
import { getEffectivePermissions } from "../../../services/admin-settings/UserPermissionService.jsx";
import { getUserGameRegistries } from "../../../services/admin-settings/UserGameService.jsx";
import { pickUsableClientId } from "../../../utils/membershipAccess.js";
import { requestPasswordReset } from "../../../services/account/PasswordResetService.jsx";

import "./SignupLogin.css";
import { SUPER_USER_LANDING } from '../../../config/accessControl.js';
import { safeReturnTo } from '../../../authentication/sessionExpiry.js';
import { OPTIONAL_LOAD_MS, SLOW_SIGN_IN_MS, USER_LOAD_MS, withinTime } from '../../../authentication/signInLoad.js';
import '../../error-pages/errorPages.css';

const Login = ({ onSwitchToSignup, navigatedFrom }) => {
    // Once Google or the password check has signed the user in: 'working' while their account loads, 'failed' when
    // it could not be. `user` is kept so Try Again loads it again without signing in again.
    const [signIn, setSignIn] = useState({ phase: 'idle', user: null });
    const [signInSlow, setSignInSlow] = useState(false);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [contentState, setContentState] = useState('login');
    const [emailUnverified, setEmailUnverified] = useState(false);
    const [resendTimer, setResendTimer] = useState(60);
    const [lastUser, setLastUser] = useState(null);
    const [isSendingReset, setIsSendingReset] = useState(false);
    const [isResendingVerification, setIsResendingVerification] = useState(false);

    // resetPassword (the Firebase client SDK call) is deliberately not pulled in here any more —
    // the reset request goes through the BFF so the server can decide what to send. AuthContext
    // still exposes it for ResetPassword.jsx, which completes the flow from the emailed link.
    const { signInWithEmailPassword, sendVerificationEmail, logOut } = UserAuth();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    useEffect(() => {
        setError('')
    }, [contentState])

    useEffect(() => {
        setSignInSlow(false);
        if (signIn.phase !== 'working') return undefined;
        const timer = setTimeout(() => setSignInSlow(true), SLOW_SIGN_IN_MS);
        return () => clearTimeout(timer);
    }, [signIn]);

    useEffect(() => {
        let timerInterval;
        if (emailUnverified && resendTimer > 0) {
            timerInterval = setInterval(() => {
                setResendTimer((prev) => prev - 1);
            }, 1000);
        }
        return () => clearInterval(timerInterval);
    }, [emailUnverified, resendTimer]);

    const handleClose = () => {
        setContentState('login');
        setEmail("");
        setPassword("");
        setEmailUnverified(false);
        setResendTimer(60);
        setLastUser(null);
    };

    // Restore persisted active context + preferences from backend, falling back to firstOrgId
    const buildDashboardContext = (userData, firstOrgId) => ({
        selectedOrgId: userData.user.activeOrgId || firstOrgId,
        selectedGameId: userData.user.activeGameId || null,
        selectedGameName: null,        // resolved by GameDecisionCenterHeader once allGames loads
        selectedGameClientId: userData.user.activeClientId || null,
        selectedRegionIds: [],
        ...buildPreferencesPatch(userData.user)
    });

    // The persisted active build may be one the user has since been suspended on. Effective
    // permissions for a suspended build resolve to NO_ACCESS, and the post-login landing
    // (getFirstAccessiblePath) would then send the user to the standalone /not-authorized page —
    // locking them out of the whole app even though they still have OTHER usable builds of the
    // same game. Repoint the active build to one they can actually open BEFORE we fetch
    // permissions. Best-effort: on any failure we keep the persisted build and let the in-app
    // auto-select cascade sort it out.
    const sanitizeActiveBuild = async (dashCtx, accessToken) => {
        if (!dashCtx.selectedOrgId || !dashCtx.selectedGameId || !dashCtx.selectedGameClientId) return;
        try {
            const data = await withinTime(getUserGameRegistries({ accessToken }), OPTIONAL_LOAD_MS);
            const usable = pickUsableClientId(
                data?.userGameRegistryDTOS || [],
                dashCtx.selectedOrgId,
                dashCtx.selectedGameId,
                dashCtx.selectedGameClientId,
            );
            // null → no usable build for this game (genuine full suspension): drop it so the
            // server resolves at game/org level (and the suspended-state page shows if warranted).
            dashCtx.selectedGameClientId = usable;
        } catch (e) {
            console.error('Could not validate persisted active build; keeping it as-is:', e);
        }
    };

    const validateEmail = (email) => {
        const re = /^[a-zA-Z0-9._%+-]+@(liquidnitrogames\.com|liquidnitro\.games)$/;
        return re.test(email);
    };

    /**
     * After Google, Microsoft or the password check has signed the user in: load their GeoPlay account and go where it
     * says. The page shows "Signing you in…" throughout, and "We couldn't finish signing you in" with Try Again when the
     * account cannot be loaded in time, so a slow server never leaves the sign-in form sitting idle.
     */
    const finishSignIn = async (user) => {
        if (navigatedFrom === 'invite') return;
        setSignIn({ phase: 'working', user });

        try {
            const userData = await withinTime(getUser({ accessToken: user.accessToken }), USER_LOAD_MS);

            if (userData && userData.user) {
                const orgs = userData.user.orgUserStates
                    ? Object.entries(userData.user.orgUserStates).map(([orgId, orgState]) => ({ orgId, orgState }))
                    : [];
                const firstOrgId = orgs.length > 0 ? orgs[0].orgId : null;
                dispatch(setUserInformation({
                    userId: userData.user.userId || null,
                    userName: userData.user.userName || null,
                    orgs: orgs,
                    roleId: userData.user.roleId || null,
                    superUser: userData.user.superUser || false
                }));
                const dashCtx = buildDashboardContext(userData, firstOrgId);
                await sanitizeActiveBuild(dashCtx, user.accessToken);
                dispatch(setDashboardContext(dashCtx));

                // Best-effort: the shell loads permissions again when they are missing, so a slow answer is skipped.
                try {
                    const perms = await withinTime(getEffectivePermissions({ accessToken: user.accessToken }), OPTIONAL_LOAD_MS);
                    dispatch(setEffectivePermissions(perms));
                } catch (permError) {
                    console.error("Error fetching effective permissions on login:", permError);
                }

                routeFromOnboardingProgress(userData, dashCtx);
            } else {
                // No server-side user record (new or deleted account). Any
                // onboarding/pending flags left in this browser are stale —
                // clear them so the user is sent to onboarding, not a phantom /demo.
                try {
                    localStorage.removeItem('gp_access_request_pending');
                } catch (_) {}
                navigate('/onboarding');
            }
        } catch (error) {
            console.error("Error loading the account after sign-in:", error);
            // Only a definitive "no such user" (HTTP 404) means this account
            // should onboard. A server/network failure (backend down, 5xx,
            // timeout, auth) must NOT drop an already-authenticated user into
            // org-creation — that's how duplicate orgs get created.
            if (error?.response?.status === 404) {
                try {
                    localStorage.removeItem('gp_access_request_pending');
                } catch (_) {}
                navigate('/onboarding');
                return;
            }
            setSignIn({ phase: 'failed', user });
        }
    };

    const signInWithAnotherAccount = async () => {
        try {
            await logOut();
        } catch (_) {}
        setSignIn({ phase: 'idle', user: null });
    };

    /**
     * Resume routing rule: saved stepper progress resumes the org/game stepper; otherwise the
     * normal landing path (or /onboarding for org-less users). Per-game setup is tracked on the
     * game itself, so nothing here mirrors it.
     */
    const routeFromOnboardingProgress = (userData, dashCtx) => {
        // Platform super-users (control plane only) never go through tenant onboarding — no org,
        // game, or SDK setup. The backend auto-provisions them from the allowlist at login, so
        // they land straight on the admin/cheats console.
        // Sent here from a page (session ended, signed-out deep link): go back to it. The route guards still decide
        // whether this user may open it.
        const returnTo = safeReturnTo(new URLSearchParams(window.location.search).get('returnTo'));
        if (userData.user?.superUser) {
            navigate(returnTo || SUPER_USER_LANDING);
            return;
        }
        const progress = userData.user?.globalPreferences?.onboardingProgress;
        if (progress && (progress.stepIdx ?? 0) > 0) {
            navigate('/onboarding', { state: { resume: progress } });
            return;
        }
        if (dashCtx.selectedOrgId) {
            navigate(returnTo || resolveLandingPath(dashCtx));
            return;
        }
        const accessPending = localStorage.getItem('gp_access_request_pending');
        navigate(accessPending ? '/demo' : '/onboarding');
    };

    const handleLogin = async (e) => {
        e.preventDefault();

        if (!email || !password) {
            setError("All fields are required");
            return;
        }

        if (!validateEmail(email)) {
            setError("Only work email addresses are allowed.");
            return;
        }
        setError("");
        setLoading(true);

        try {
            const userCredential = await signInWithEmailPassword(email, password);

            if (navigatedFrom !== 'invite') {
                // Independent of whether a user record exists — gating on that let anyone who had
                // already onboarded log in unverified.
                if (!isEmailVerified(userCredential.user)) {
                    setLastUser(userCredential.user);
                    setLoading(false);
                    setError("Email verification is still pending. Please check your inbox.");
                    setEmailUnverified(true);
                    setResendTimer(0);
                    return;
                }

                handleClose();
                setLoading(false);
                await finishSignIn(userCredential.user);
                return;
            }

            setLoading(false);
        } catch (error) {
            // A Firebase auth error carries `error.code`; anything else here is
            // a backend/network failure (e.g. getUser when the server is down).
            // Distinguish them so we don't blame the user's credentials for a
            // server outage — and we never route to onboarding from this path.
            let errorMessage = error.code
                ? "Login failed. Please try again."
                : "We couldn't reach the server. Please try again in a moment.";
            if (error.code) {
                switch (error.code) {
                    case 'auth/invalid-email':
                        errorMessage = "Email address not found.";
                        break;
                    case 'auth/invalid-credential':
                        errorMessage = "Invalid email or password. Please check and try again.";
                        break;
                    case 'auth/user-not-found':
                        errorMessage = "No account found with this email address.";
                        break;
                    case 'auth/wrong-password':
                        errorMessage = "Incorrect password. Please try again.";
                        break;
                    case 'auth/too-many-requests':
                        errorMessage = "Too many login attempts. Please try again later.";
                        break;
                    case 'auth/account-exists-with-different-credential':
                        errorMessage = "The account already exists with a different credential.";
                        break;
                    case 'auth/user-disabled':
                        errorMessage = "This account has been disabled. Please contact support.";
                        break;
                    default:
                        break;
                }
            }
            setError(errorMessage);
            setLoading(false);
        }
    };

    const handleResetPassword = async () => {
        if(!email) {
            setError('Email is required');
            return
        }
        if(!validateEmail(email)) {
            setError('Email is invalid. Only work email addresses are allowed.');
            return
        }
        setIsSendingReset(true);
        try {
            // Server-side rather than Firebase's client SDK: only the server can see whether this
            // address has an account and whether that account has a password at all, and it answers
            // that question by email instead of on screen. The SDK call could only ever send a reset
            // link or silently do nothing, which is what left people staring at a mail that never
            // came. The response is identical for every address, so there is nothing to branch on.
            await requestPasswordReset(email);
            setContentState('resetSent');
        } catch (err) {
            // Only a transport failure can land here — the endpoint returns 200 for every address.
            setError("We couldn't submit that request. Please try again in a moment.");
        } finally {
            setIsSendingReset(false);
        }
    }

    const handleForgotPasswordButtonClick = () => {
        setContentState('resetEmail');
    }

    const handleEmailVerificationLinkResend = async (user) => {
        setIsResendingVerification(true);
        // The cooldown starts on the tap, not when the two round-trips below finish. The screen
        // switches to the "check your email" panel immediately, and that panel shows its "Resend
        // Link" control whenever the timer reads 0 — so starting the timer after the awaits left
        // the link visible and live (good for another full re-send) for as long as the send took.
        // Rolled back in the catch so a failed send can be retried at once.
        setResendTimer(60);
        if (contentState === 'login') setContentState('emailSentSuccessfully');
        try {
            const userCredential = await signInWithEmailPassword(email, password);
            setLastUser(userCredential.user);
            await sendVerificationEmail(userCredential.user);
            setError("");
        } catch (error) {
            setResendTimer(0);
            setError("Failed to resend verification email. Please try again.");
        } finally {
            setIsResendingVerification(false);
        }
    };


    const loginComponent = () => {
        return (
            <div className="login-signup-form-container">
                <div className="login-signup-form-super-title-row">
                    <span className="login-signup-brand-mark"><GeoPlayLogo /></span>
                    <h2 className="login-signup-form-super-title">Login</h2>
                </div>
                <ThirdPartyLogins
                    onSuccess={async (user) => {
                        handleClose();
                        await finishSignIn(user);
                    }}
                    onError={(message) => {
                        setError(message);
                        setLoading(false);
                    }}
                    disabled={loading}
                />
                <div className="login-signup-divider">OR</div>
                <Input
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    variant="email"
                    placeholder="Enter Email"
                    label='Email'
                    isRequired
                    icon={<FontAwesomeIcon icon={faEnvelope} />}
                />
                <Password
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Password"
                    label='Password'
                    isRequired
                />
                {error && <div className="error-message">{error}</div>}
                {emailUnverified && (
                    <>
                        <p className="login-signup-form-helptext">Didn't receive the verification link?</p>
                        <div className="login-signup-right-align-button">
                            <TertiaryButton onClick={() => handleEmailVerificationLinkResend(lastUser)} disabled={isResendingVerification} tone="primary">
                                {isResendingVerification ? 'Sending...' : 'Resend Verification Link'}
                            </TertiaryButton>
                        </div>
                    </>
                )}
                <div className="login-signup-right-align-button">
                    <TertiaryButton onClick={handleForgotPasswordButtonClick} tone="primary">
                        Forgot Password
                    </TertiaryButton>
                </div>
                <PrimaryButton
                    onClick={handleLogin}
                    disabled={loading}
                    isEnterTriggered={true}
                    isFullWidth
                >
                    {loading ? "Logging In..." : "Login"}
                </PrimaryButton>
                {onSwitchToSignup && (
                    <div className="login-signup-form-container-section-row">
                        <p className="login-signup-form-helptext">No account?</p>
                        <SecondaryButton onClick={onSwitchToSignup} isFullWidth>
                            Sign Up
                        </SecondaryButton>
                    </div>
                )}
            </div>
        );
    };
    
    const passwordResetComponent = () => {
        return (
            <div className="login-signup-form-container">
                <h2 className="login-signup-form-title left-align">Forgot Password</h2>
                <p className="login-signup-form-helptext">The password reset link will be sent via email, so the email must be active and match the one registered.</p>
                {/* Stated up front rather than discovered by waiting for a mail that never comes.
                    An account created through Google or Microsoft has no password credential to
                    reset, and we cannot say so per-address without confirming which addresses have
                    accounts — so say it to everyone. */}
                <p className="login-signup-form-helptext">Signed up with Google or Microsoft? Those accounts have no password to reset — go back and use that button to sign in.</p>
                <Input
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    variant="email"
                    placeholder="Enter Email"
                    label='Enter Email'
                    isRequired
                    icon={<FontAwesomeIcon icon={faEnvelope} />}
                />
                {error && <div className="error-message">{error}</div>}
                <div className="login-signup-form-container-section-row">
                    <SecondaryButton onClick={() => setContentState('login')} isFullWidth>
                        Back To Login
                    </SecondaryButton>
                    <PrimaryButton onClick={handleResetPassword} disabled={isSendingReset} isEnterTriggered isFullWidth>
                        {isSendingReset ? 'Sending...' : 'Send Reset Link'}
                    </PrimaryButton>
                </div>
            </div>
        )
    }

    const passwordResetSentComponent = () => {
        return (
            <div className="login-signup-form-container">
                <div className="login-signup-form-icon-container">
                    <EmailIcon/>
                </div>
                {/* Deliberately does NOT confirm the address has an account. Firebase's email
                    enumeration protection resolves this call successfully for unknown addresses and
                    sends nothing, so "we sent you a link" would be a claim we cannot make — and
                    reporting "no such account" here would turn this form into an account-enumeration
                    oracle for anyone probing company email addresses. */}
                <h2 className="login-signup-form-title">Check your email</h2>
                <p className="login-signup-form-helptext">If an account exists for this address, we’ve sent a password reset link to:</p>
                <div className="login-signup-form-email-badge">{email || ''}</div>
                <SecondaryButton onClick={handleEmailVerificationClose} isFullWidth>Back To Home Page</SecondaryButton>
                {/* Reset copy, not the sign-up verification line this screen used to borrow — nothing
                    here is about verifying an account, and there is no resend control on this screen,
                    so point back at Forgot Password instead. */}
                <p className="login-signup-form-helptext">Please check your inbox (and spam folder). If no link arrives, that address either has no account or signs in with Google or Microsoft — try that button on the sign-in screen, or sign up.</p>
            </div>
        )
    }

    const handleEmailVerificationClose = async () => {
        await logOut();
        handleClose();
        navigate('/');
    }

    const emailSentSuccessfully = () => {
        return (
            <div className="login-signup-form-container">
                <div className="login-signup-form-icon-container">
                    <EmailIcon/>
                </div>
                <h2 className="login-signup-form-title">Verify Your Email</h2>
                <p className="login-signup-form-helptext">We’ve successfully registered your account. A verification link has been dispatched to:</p>
                <div className="login-signup-form-email-badge">{email || ''}</div>
                {error && <div className="error-message">{error}</div>}
                <p className="login-signup-form-helptext">Didn't receive the link?</p>
                {resendTimer ?
                    <div className="login-signup-form-retry">
                        <FontAwesomeIcon icon={faClock} />
                        Try again in <span>{resendTimer}s</span>
                    </div>
                    : <LinkButton onClick={() => handleEmailVerificationLinkResend(lastUser)}>Resend Link</LinkButton>}
                <SecondaryButton onClick={handleEmailVerificationClose} isFullWidth>OK</SecondaryButton>
                <p className="login-signup-form-helptext">Please check your inbox (and spam folder). You must verify account to access all platform features.</p>
            </div>
        );
    }

    const signingInComponent = () => (
        <div className="login-signup-form-container login-signing-in" role="status">
            <div className="loading-spinner-ring" aria-hidden="true">
                <div className="loading-ring-track"></div>
                <div className="loading-ring-fill"></div>
            </div>
            <h2 className="login-signup-form-title">Signing You In…</h2>
            <p className="login-signup-form-helptext">
                {signInSlow ? 'Still loading your workspace. This is taking longer than usual.' : 'Loading your workspace.'}
            </p>
        </div>
    );

    const signInFailedComponent = () => (
        <div className="login-signup-form-container" role="alert">
            <h2 className="login-signup-form-title">We Couldn't Finish Signing You In</h2>
            <p className="login-signup-form-helptext">
                You're signed in, but we couldn't load your workspace. Check your connection, then try again.
            </p>
            <PrimaryButton onClick={() => finishSignIn(signIn.user)} isFullWidth>Try Again</PrimaryButton>
            <SecondaryButton onClick={signInWithAnotherAccount} isFullWidth>Use a Different Account</SecondaryButton>
        </div>
    );

    const renderModalContent = () => {
        if (signIn.phase === 'working') return signingInComponent();
        if (signIn.phase === 'failed') return signInFailedComponent();
        switch (contentState) {
            case 'login':
                return loginComponent();
            case 'emailSentSuccessfully':
                return emailSentSuccessfully();
            case 'resetEmail':
                return passwordResetComponent();
            case 'resetSent':
                return passwordResetSentComponent();
            default:
                return loginComponent();
        }
    }

    return (
        <div className="login-signup-page">
            {renderModalContent()}
        </div>
    );
};

export default Login;